import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabaseClient } from '../../lib/supabase';
import { HOTEL_ID } from '../../lib/supabase/configClient';
import { logger } from '../../lib/logger';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { Bell, X } from 'lucide-react';
import { resolveColor } from '../../utils/colorUtils';
import { stayService } from '../../lib/stay/stayService';
import { navigationClient } from '../../lib/navigation';

interface AlertTranslation {
  language: string;
  content: string;
}

interface Alert {
  id: string;
  content: string;
  url: string | null;
  title: string;
  start_time: string | null;
  end_time: string | null;
  translations: AlertTranslation[];
  color_alert_back: string | null; // Background color for the alert
  color_alert_txt: string | null; // Text color for the alert
  banner_size: 'small' | 'big' | null; // Size of the banner
  alert_type: 'standard' | 'checkout' | null; // Type of alert
  action_type: 'url' | 'section' | null; // Type of action when clicked
  action_section_id: string | null; // Section ID to navigate to when clicked
}

export const ActiveAlertBanner: React.FC<{ config: any }> = ({ config }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [alert, setAlert] = useState<Alert | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [isPwaPromptVisible, setIsPwaPromptVisible] = useState(false);
  const [checkoutAlertShown, setCheckoutAlertShown] = useState(false);

  // Get navigation data for section navigation
  const { data: navigation } = useQuery({
    queryKey: ['navigation'],
    queryFn: () => navigationClient.getNavigation(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  // Check if PWA install prompt is visible
  useEffect(() => {
    const hasVisited = localStorage.getItem('has_visited');
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    
    // PWA prompt is visible if user hasn't visited before and isn't in PWA mode
    setIsPwaPromptVisible(!hasVisited && !isPWA);
  }, []);

  // Check if today is the last day of the stay
  const isCheckoutDay = (): boolean => {
    const stayDates = stayService.getStayDates();
    if (!stayDates) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const endDate = new Date(stayDates.endDate);
    endDate.setHours(0, 0, 0, 0); // Reset time to start of day
    
    // Check if today is the checkout day
    return today.getTime() === endDate.getTime();
  };

  // Check if checkout alert has already been shown today
  const hasCheckoutAlertBeenShownToday = (): boolean => {
    return stayService.hasCheckoutAlertBeenShownToday();
  };

  // Mark checkout alert as shown for today
  const markCheckoutAlertAsShown = (): void => {
    stayService.markCheckoutAlertAsShown();
    setCheckoutAlertShown(true);
  };

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setIsLoading(true);
        const now = new Date();
        const currentDate = now.toISOString().split('T')[0]; // YYYY-MM-DD
        const currentTime = now.toTimeString().split(' ')[0]; // HH:MM:SS

        // Check if today is checkout day and alert hasn't been shown yet
        const shouldShowCheckoutAlert = isCheckoutDay() && !hasCheckoutAlertBeenShownToday();
        
        logger.debug('Fetching active alerts', {
          category: 'Alerts',
          data: { 
            hotelId: HOTEL_ID,
            date: currentDate,
            time: currentTime,
            isCheckoutDay: isCheckoutDay(),
            hasCheckoutAlertBeenShownToday: hasCheckoutAlertBeenShownToday(),
            shouldShowCheckoutAlert
          }
        });

        // Call the RPC with the appropriate parameters
        const response = await supabaseClient.rpc('get_active_alerts', {
          p_hotel_id: HOTEL_ID,
          p_date: currentDate,
          p_time: currentTime,
          p_checkout: shouldShowCheckoutAlert // Only request checkout alerts if needed
        });

        const { data, error } = response;

        if (error) {
          throw error;
        }

        if (data && data.length > 0) {
          logger.debug('Active alerts found', {
            category: 'Alerts',
            data: { 
              count: data.length,
              firstAlert: {
                id: data[0].id,
                content: data[0].content?.substring(0, 50) + '...',
                hasUrl: !!data[0].url,
                translationsCount: data[0].translations?.length || 0,
                color_alert_back: data[0].color_alert_back,
                color_alert_txt: data[0].color_alert_txt,
                banner_size: data[0].banner_size,
                alert_type: data[0].alert_type,
                action_type: data[0].action_type,
                action_section_id: data[0].action_section_id
              }
            }
          });
          
          // If this is a checkout alert, mark it as shown for today
          if (data[0].alert_type === 'checkout') {
            markCheckoutAlertAsShown();
          }
          
          setAlert(data[0]); // Take the first active alert
          
          // Track alert impression
          analyticsService.trackEvent({
            event_type: 'impression',
            event_name: 'alert_banner_shown',
            event_data: {
              alert_id: data[0].id,
              has_url: !!data[0].url,
              banner_size: data[0].banner_size || 'small',
              alert_type: data[0].alert_type || 'standard',
              action_type: data[0].action_type || 'url'
            }
          });
        } else {
          logger.debug('No active alerts found', {
            category: 'Alerts'
          });
          setAlert(null);
        }
      } catch (error) {
        logger.error('Error fetching alerts', {
          category: 'Alerts',
          data: { error }
        });
        console.error('Error fetching alerts:', error);
        setAlert(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAlerts();

    // Set up a timer to refresh alerts every 5 minutes
    const intervalId = setInterval(fetchAlerts, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Refresh when language changes to update translations
  useEffect(() => {
    if (alert) {
      logger.debug('Language changed, updating alert content', {
        category: 'Alerts',
        data: { 
          alertId: alert.id,
          language: i18n.language,
          availableTranslations: alert.translations?.map(t => t.language) || []
        }
      });
    }
  }, [i18n.language, alert]);

  // Don't show anything if loading, no alert, or dismissed
  if (isLoading || !alert || dismissed) return null;

  // Don't show big banner if PWA prompt is visible
  if (alert.banner_size === 'big' && isPwaPromptVisible) return null;

  // Find the translation for the current language or fall back to default content
  const translatedContent = alert.translations?.find(
    (t) => t.language === i18n.language
  )?.content || alert.content;

  // Resolve colors - can be hex values or theme references
  const backgroundColor = resolveColor(alert.color_alert_back, config, config['major-color']);
  const textColor = resolveColor(alert.color_alert_txt, config, config['light-color']);

  const handleClick = () => {
    // Check action type to determine behavior
    if (alert.action_type === 'section' && alert.action_section_id && navigation) {
      // Find the section in navigation data
      const section = navigation.find(item => item.id === alert.action_section_id);
      
      if (section) {
        // Track section navigation click
        analyticsService.trackEvent({
          event_type: 'interaction',
          event_name: 'alert_banner_click',
          event_data: {
            alert_id: alert.id,
            action_type: 'section',
            section_id: alert.action_section_id,
            section_name: section.names.fr,
            section_type: section.type_rubrique,
            banner_size: alert.banner_size || 'small',
            alert_type: alert.alert_type || 'standard'
          }
        });
        
        // Dismiss the alert
        setDismissed(true);
        
        // Generate slug and navigate to the section
        const slug = navigationClient.generateSlug(section.names.fr);
        navigate(`/${slug}`);
      } else {
        logger.error('Section not found for navigation', {
          category: 'Alerts',
          data: { 
            sectionId: alert.action_section_id,
            availableSections: navigation.map(n => ({ id: n.id, name: n.names.fr }))
          }
        });
      }
    } else if (alert.url) {
      // Default URL behavior
      analyticsService.trackEvent({
        event_type: 'interaction',
        event_name: 'alert_banner_click',
        event_data: {
          alert_id: alert.id,
          url: alert.url,
          action_type: 'url',
          banner_size: alert.banner_size || 'small',
          alert_type: alert.alert_type || 'standard'
        }
      });
      
      // Dismiss the alert
      setDismissed(true);
      
      window.open(alert.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the banner click
    
    // Track alert dismissal
    analyticsService.trackEvent({
      event_type: 'interaction',
      event_name: 'alert_banner_dismiss',
      event_data: {
        alert_id: alert.id,
        banner_size: alert.banner_size || 'small',
        alert_type: alert.alert_type || 'standard',
        action_type: alert.action_type || 'url'
      }
    });
    
    setDismissed(true);
  };

  // Determine if the banner is clickable
  const isClickable = alert.url || (alert.action_type === 'section' && alert.action_section_id);

  // Render different banner based on size
  if (alert.banner_size === 'big') {
    return (
      <div
        className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black bg-opacity-50"
        onClick={handleDismiss}
      >
        <div
          className={`max-w-lg w-full p-6 rounded-lg shadow-xl text-center ${
            isClickable ? 'cursor-pointer hover:brightness-95' : ''
          }`}
          style={{ 
            backgroundColor,
            color: textColor,
            borderRadius: config.border_radius
          }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent closing when clicking the banner
            if (isClickable) handleClick();
          }}
        >
          <div className="flex items-start justify-between mb-4">
            <Bell size={24} className="shrink-0" />
            <div className="mx-auto"></div>
            <button
              onClick={handleDismiss}
              className="shrink-0 p-1 hover:bg-white/10 rounded-full transition-colors"
              aria-label="Close alert"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="mt-4 pb-4 text-xl font-bold">
            {translatedContent}
          </div>
        </div>
      </div>
    );
  }

  // Default small banner
  return (
    <div
      className={`fixed top-[50px] left-0 right-0 z-[150] py-3 px-4 transition-all duration-200 ${
        isClickable ? 'cursor-pointer hover:brightness-95' : ''
      }`}
      style={{ 
        backgroundColor,
        color: textColor
      }}
      onClick={isClickable ? handleClick : undefined}
    >
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        <div className="flex items-center gap-2 text-left">
          <Bell size={16} className="shrink-0" />
          <span>{translatedContent}</span>
        </div>
        
        <button
          onClick={handleDismiss}
          className="shrink-0 p-1 hover:bg-white/10 rounded-full transition-colors"
          aria-label="Close alert"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};